/**
 * Postgres / PostgREST → the error-code contract in `@avexa/types`.
 *
 * WHY THIS FILE IS NOT OPTIONAL.
 *
 * A failed write in this schema arrives as one of three very different things,
 * and all three look identical to a `catch` block:
 *
 *   1. A CONSTRAINT the database refused — `23505` unique, `23514` check,
 *      `23503` foreign key. The constraint NAME is the only reliable signal of
 *      what actually went wrong; the message around it is English prose that
 *      changes between Postgres releases.
 *   2. An RLS DENIAL — `42501` on a write, or simply zero rows on a read.
 *      Postgres does not distinguish "you may not see this" from "it does not
 *      exist", and neither does this mapper, on purpose: telling a stranger
 *      that a row exists but is not theirs is itself a disclosure.
 *   3. A TRIGGER that raised. Migrations 003, 005, 007 and 008 raise with an
 *      explicit `errcode`, so a raised exception is indistinguishable from a
 *      real constraint failure by SQLSTATE alone. The message text is matched
 *      as a second key — see MESSAGE_SIGNATURES.
 *
 * The client never renders `PostgrestError.message`. It is English, it quotes
 * constraint names and row ids, and Docs/05 § 6 is explicit that only the CODE
 * crosses into the UI, where `errorTranslationKey()` turns it into a string the
 * user's locale actually has.
 */

import type { PostgrestError } from '@supabase/supabase-js'
import {
  ERROR_CODES,
  errorTranslationKey,
  isRetryable,
  type ErrorCode,
  type ErrorDetails,
} from '@avexa/types'

/**
 * The single error type every function in this package throws.
 *
 * `message` is deliberately developer-facing and safe to log (Sentry, a console
 * in development). `code` is the only field a UI is allowed to branch on.
 */
export class AvexaApiError extends Error {
  readonly code: ErrorCode
  readonly details: ErrorDetails
  /**
   * The original PostgrestError / AuthError / TypeError, for logging only.
   * Overrides `Error.cause` (ES2022) so a logger that already understands the
   * standard field picks it up without knowing about this class.
   */
  override readonly cause: unknown
  /** True when retrying the identical request could plausibly succeed. */
  readonly retryable: boolean

  constructor(code: ErrorCode, message: string, details: ErrorDetails = {}, cause?: unknown) {
    super(message)
    this.name = 'AvexaApiError'
    this.code = code
    this.details = details
    this.cause = cause
    this.retryable = isRetryable(code)
    // Restores the prototype chain when this file is transpiled down to ES5 by
    // a consumer's bundler; without it `instanceof AvexaApiError` returns false.
    Object.setPrototypeOf(this, AvexaApiError.prototype)
  }

  /** `errors.SLUG_TAKEN` — the i18n key the UI renders instead of `message`. */
  get translationKey(): `errors.${ErrorCode}` {
    return errorTranslationKey(this.code)
  }
}

export function isAvexaApiError(value: unknown): value is AvexaApiError {
  return value instanceof AvexaApiError
}

// ─── Constraint and SQLSTATE tables ─────────────────────────────────────────

/**
 * SQLSTATE → code, for the failures whose meaning does not depend on which
 * constraint fired. Anything constraint-specific is resolved first, below.
 *
 * PGRST* are PostgREST's own codes, not Postgres SQLSTATEs; they share the
 * field, so they share the table.
 */
const SQLSTATE_CODES: Readonly<Record<string, ErrorCode>> = {
  // ── Postgres ──────────────────────────────────────────────────────────────
  '23502': ERROR_CODES.VALIDATION_FAILED, // not_null_violation
  '23503': ERROR_CODES.NOT_FOUND, // foreign_key_violation — the referenced row is gone
  '23505': ERROR_CODES.ALREADY_EXISTS, // unique_violation
  '23514': ERROR_CODES.VALIDATION_FAILED, // check_violation
  // exclusion_violation. The ONLY exclusion constraint in this schema is
  // `bookings_no_overlap`, so the SQLSTATE alone is unambiguous — and it is the
  // race that a SELECT-then-INSERT check cannot catch, which is precisely why
  // migration 009 pushed it into the database.
  '23P01': ERROR_CODES.DOUBLE_BOOKING,
  '22001': ERROR_CODES.VALIDATION_FAILED, // string_data_right_truncation
  '22003': ERROR_CODES.VALIDATION_FAILED, // numeric_value_out_of_range
  '22007': ERROR_CODES.VALIDATION_FAILED, // invalid_datetime_format
  '22P02': ERROR_CODES.VALIDATION_FAILED, // invalid_text_representation (a malformed uuid)
  '42501': ERROR_CODES.FORBIDDEN, // insufficient_privilege — RLS, or a raising trigger
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * 🔴 `no_data_found` IS **P0002**, NOT `02000`, AND THIS BLOCK CLAIMED
   *    OTHERWISE FROM THE DAY IT WAS WRITTEN. MEASURED 9 OCT 2026.
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * `raise exception … using errcode = 'no_data_found'` in PL/pgSQL produces
   * SQLSTATE **P0002** — the PL/pgSQL-specific `NO_DATA_FOUND`. `02000` is the
   * SQL-standard `no_data`, which is a different condition and is **not what any
   * of these functions emit.** So for as long as only `02000` was mapped, every
   * one of the twenty-six `no_data_found` raises in this schema fell through to
   * `INTERNAL_ERROR` and came back as **HTTP 500** — the same answer a dead
   * connection gives — and the reasoning written below was aspirational rather
   * than true.
   *
   * ⚠️ IT WAS FOUND BY A PROBE THAT EXPECTED `02000` AND GOT `500 P0002`, not by
   * reading. Two calls measured over HTTP as real signed-in users:
   * `request_payout()` → *"Add a bank account before asking for a payout"* and
   * `release_escrow()` → *"No money is held for this job"*. **This is the general
   * lesson in CLAUDE.md § 7 again: the comment agreed with itself, and nothing
   * errored.**
   *
   * 🔴 **FIXING IT CHANGES BEHAVIOUR IN MODULES THIS DID NOT TOUCH, AND THAT IS
   * THE POINT RATHER THAN A SIDE EFFECT.** Every mapper that already branches on
   * `GONE` — `disputeWriteFailure`, `claimReviewFailure`, the identity and payout
   * ones — was written against the paragraph below and has been taking the
   * `failed` branch instead. They now behave as their own comments say.
   *
   * `02000` stays mapped beside it: a genuine SQL `no_data` means the same thing
   * to a caller, and removing it would be a second guess.
   *
   * ─────────────────────────────────────────────────────────────────────────
   *
   * no_data_found. Three claim functions raise it deliberately (migration 045),
   * all meaning "the row you named is not in a state this transition applies
   * to":
   *
   *   review_business_claim()    another operator already decided this claim
   *   withdraw_business_claim()  no OPEN claim of yours with that id
   *   answer_business_claim()    not yours, or not waiting for information
   *
   * ─── WHY `GONE` AND NOT `NOT_FOUND` ────────────────────────────────────
   *
   * With no entry here it fell through to INTERNAL_ERROR — the same code a
   * dead connection produces — so the admin drawer had to tell an operator
   * "either somebody else got there first, or the request failed", which is
   * two very different next actions in one sentence.
   *
   * `NOT_FOUND` is the obvious choice and it is the wrong one. The dominant
   * case by far is a colleague approving the claim while this operator had the
   * drawer open, and "that claim does not exist" is false about a claim that
   * plainly does. `GONE` says what actually happened: it was there, the window
   * to act on it has closed. This is its first use in the codebase.
   *
   * ⚠️ IT IS NOT EXACT, AND THE IMPRECISION IS RECORDED RATHER THAN HIDDEN.
   * `withdraw_business_claim()` raises the same SQLSTATE for a genuinely
   * unknown id, which is `NOT_FOUND`'s case, not `GONE`'s. A client cannot
   * tell them apart from the SQLSTATE alone, and it does not matter yet: the
   * unknown-id path is reachable only from a hand-written request, while the
   * already-decided path is reachable by two admins doing their job. If that
   * ever stops being true, the fix is distinct errcodes in the three
   * functions, not a different mapping here.
   *
   * Docs/15 Q78.
   */
  '02000': ERROR_CODES.GONE,
  // 🔴 THE ONE THAT ACTUALLY FIRES. See the block above: `no_data_found` raised
  // from PL/pgSQL is P0002, and all twenty-six raises in this schema use it.
  P0002: ERROR_CODES.GONE,
  '42P01': ERROR_CODES.INTERNAL_ERROR, // undefined_table — a migration has not run
  '42883': ERROR_CODES.INTERNAL_ERROR, // undefined_function — an RPC has not been deployed
  '42P17': ERROR_CODES.INTERNAL_ERROR, // infinite recursion in a policy (CLAUDE.md § 7)
  P0001: ERROR_CODES.VALIDATION_FAILED, // raise_exception with no explicit errcode
  '40001': ERROR_CODES.INTERNAL_ERROR, // serialization_failure — retryable
  '40P01': ERROR_CODES.INTERNAL_ERROR, // deadlock_detected — retryable
  '53300': ERROR_CODES.SERVICE_UNAVAILABLE, // too_many_connections
  '53400': ERROR_CODES.SERVICE_UNAVAILABLE, // configuration_limit_exceeded
  '57014': ERROR_CODES.SERVICE_UNAVAILABLE, // query_canceled (statement timeout)
  '58030': ERROR_CODES.SERVICE_UNAVAILABLE, // io_error

  // ── PostgREST ─────────────────────────────────────────────────────────────
  // "JSON object requested, multiple (or no) rows returned" — what `.single()`
  // returns when RLS filtered the row away. Indistinguishable from absent, and
  // reported as absent.
  PGRST116: ERROR_CODES.NOT_FOUND,
  PGRST202: ERROR_CODES.INTERNAL_ERROR, // the RPC does not exist in the schema cache
  PGRST204: ERROR_CODES.INTERNAL_ERROR, // the column does not exist in the schema cache
  PGRST301: ERROR_CODES.SESSION_EXPIRED, // the JWT is expired or unverifiable
  PGRST302: ERROR_CODES.UNAUTHENTICATED, // anonymous access is disabled for this route
}

/**
 * Constraint name → code.
 *
 * Every key is a real index or CHECK from `supabase/migrations`. Matching on the
 * name rather than the prose is what makes this stable: a constraint name only
 * changes in a migration, which is a change we make deliberately.
 */
const CONSTRAINT_CODES: Readonly<Record<string, ErrorCode>> = {
  // 003 — businesses
  businesses_slug_key: ERROR_CODES.SLUG_TAKEN,
  businesses_org_number_key: ERROR_CODES.ORG_NUMBER_TAKEN,
  businesses_org_number_format: ERROR_CODES.INVALID_ORG_NUMBER,
  businesses_status_valid: ERROR_CODES.VALIDATION_FAILED,
  businesses_founded_year_sane: ERROR_CODES.VALIDATION_FAILED,
  businesses_name_not_blank: ERROR_CODES.VALIDATION_FAILED,
  business_members_unique_user: ERROR_CODES.ALREADY_EXISTS,
  business_locations_one_primary: ERROR_CODES.ALREADY_EXISTS,
  service_areas_shape_complete: ERROR_CODES.VALIDATION_FAILED,
  service_areas_radius_sane: ERROR_CODES.VALIDATION_FAILED,
  opening_hours_order: ERROR_CODES.INVALID_DATE_RANGE,
  opening_hours_times_present: ERROR_CODES.VALIDATION_FAILED,
  availability_overrides_unique: ERROR_CODES.ALREADY_EXISTS,

  // 005 — services
  services_price_present: ERROR_CODES.INVALID_PRICE,
  services_price_non_negative: ERROR_CODES.INVALID_PRICE,
  services_price_range_ordered: ERROR_CODES.INVALID_PRICE,
  services_free_is_zero: ERROR_CODES.INVALID_PRICE,
  services_bookable_has_duration: ERROR_CODES.VALIDATION_FAILED,
  services_duration_sane: ERROR_CODES.VALIDATION_FAILED,
  certifications_dates_ordered: ERROR_CODES.INVALID_DATE_RANGE,

  // 006 — content
  posts_slug_unique: ERROR_CODES.SLUG_TAKEN,
  posts_offer_dates_ordered: ERROR_CODES.INVALID_DATE_RANGE,
  posts_discount_range: ERROR_CODES.VALIDATION_FAILED,
  posts_has_content: ERROR_CODES.VALIDATION_FAILED,
  post_likes_pkey: ERROR_CODES.ALREADY_EXISTS,
  follows_pkey: ERROR_CODES.ALREADY_EXISTS,
  saved_items_user_id_item_type_item_id_key: ERROR_CODES.ALREADY_EXISTS,

  // 007 — requests, offers, jobs
  requests_budget_ordered: ERROR_CODES.INVALID_PRICE,
  requests_dates_ordered: ERROR_CODES.INVALID_DATE_RANGE,
  requests_locatable: ERROR_CODES.VALIDATION_FAILED,
  requests_business_kinds_have_business: ERROR_CODES.VALIDATION_FAILED,
  // The partial unique index behind "one live offer per business per request".
  offers_one_active_per_business: ERROR_CODES.OFFER_ALREADY_SENT,
  offers_vat_rate_valid: ERROR_CODES.INVALID_VAT_RATE,
  offers_price_non_negative: ERROR_CODES.INVALID_PRICE,
  offers_available_ordered: ERROR_CODES.INVALID_DATE_RANGE,
  offer_line_items_vat_valid: ERROR_CODES.INVALID_VAT_RATE,
  offer_line_items_price_non_negative: ERROR_CODES.INVALID_PRICE,
  offer_line_items_quantity_positive: ERROR_CODES.VALIDATION_FAILED,
  // Two-sided completion: one party alone cannot finish a job.
  jobs_completed_requires_both: ERROR_CODES.AWAITING_COUNTERPARTY,
  request_matches_request_id_business_id_key: ERROR_CODES.ALREADY_EXISTS,

  // 008 — messaging
  messages_text_has_body: ERROR_CODES.VALIDATION_FAILED,
  messages_body_length: ERROR_CODES.VALIDATION_FAILED,
  conversation_participants_role_valid: ERROR_CODES.VALIDATION_FAILED,
  internal_notes_body_not_blank: ERROR_CODES.VALIDATION_FAILED,

  // 009 — bookings and reviews
  bookings_no_overlap: ERROR_CODES.DOUBLE_BOOKING,
  bookings_ends_after_starts: ERROR_CODES.INVALID_DATE_RANGE,
  bookings_duration_sane: ERROR_CODES.INVALID_DATE_RANGE,
  bookings_price_non_negative: ERROR_CODES.INVALID_PRICE,
  bookings_cancelled_has_who: ERROR_CODES.VALIDATION_FAILED,
  // One review per customer per job, and per booking. Without these a customer
  // can review the same work repeatedly and move the average on their own.
  reviews_one_per_job: ERROR_CODES.ALREADY_REVIEWED,
  reviews_one_per_booking: ERROR_CODES.ALREADY_REVIEWED,
  reviews_rating_range: ERROR_CODES.VALIDATION_FAILED,
  reviews_body_length: ERROR_CODES.VALIDATION_FAILED,
  reviews_status_valid: ERROR_CODES.VALIDATION_FAILED,
  // One response per review — a business answers, it does not get a thread.
  review_responses_review_id_key: ERROR_CODES.ALREADY_EXISTS,
  review_responses_body_not_blank: ERROR_CODES.VALIDATION_FAILED,
  review_helpful_pkey: ERROR_CODES.ALREADY_EXISTS,
  review_requests_one_per_job: ERROR_CODES.ALREADY_EXISTS,
  review_requests_one_per_booking: ERROR_CODES.ALREADY_EXISTS,

  // 010 — notifications
  notification_deliveries_once: ERROR_CODES.ALREADY_EXISTS,
  notification_deliveries_status_valid: ERROR_CODES.VALIDATION_FAILED,

  // 002 — identity
  profiles_referral_code_key: ERROR_CODES.ALREADY_EXISTS,
  user_settings_radius_range: ERROR_CODES.VALIDATION_FAILED,
  user_settings_theme_valid: ERROR_CODES.VALIDATION_FAILED,
  blocked_users_not_self: ERROR_CODES.VALIDATION_FAILED,
  user_devices_expo_push_token_key: ERROR_CODES.ALREADY_EXISTS,
  user_devices_platform_valid: ERROR_CODES.VALIDATION_FAILED,
}

/**
 * Message fragments for the trigger-raised exceptions.
 *
 * These triggers raise with a BORROWED errcode (`check_violation`,
 * `insufficient_privilege`) because Postgres has no code for "you are the last
 * owner". The SQLSTATE alone would therefore map them to the generic answer, so
 * the prose is the second key.
 *
 * Matching English prose is fragile, which is precisely why it is the LAST
 * resort and why each entry names the function that raises it: if a migration
 * rewords one of these, the mapping degrades to the SQLSTATE default rather
 * than breaking. Lower-cased before comparison.
 */
const MESSAGE_SIGNATURES: readonly (readonly [fragment: string, code: ErrorCode])[] = [
  // protect_business_owner() — migration 003
  ['at least one active owner', ERROR_CODES.LAST_OWNER],
  // on_offer_accepted() — migration 007
  ['already been awarded', ERROR_CODES.REQUEST_ALREADY_AWARDED],
  // mark_conversation_read() — migration 008
  ['not a participant in this conversation', ERROR_CODES.FORBIDDEN],
  // protect_certification_verification() — migration 005
  ['verification status of a certification', ERROR_CODES.ADMIN_ONLY],
  // maintain_category_path() — migration 004
  ['parent category', ERROR_CODES.NOT_FOUND],
  // get_available_slots() — migration 009
  ['date range must be forward', ERROR_CODES.INVALID_DATE_RANGE],
  /*
   * book_slot() — migration 110. All three raise `check_violation`, which the
   * SQLSTATE table below maps to VALIDATION_FAILED — correct for a form field
   * and useless to a booking screen, whose whole remedy is "re-fetch the day".
   *
   * ⚠️ SLOT_UNAVAILABLE and DOUBLE_BOOKING ARE NOT THE SAME ANSWER, and the
   * screen says different things for them. SLOT_UNAVAILABLE means the instant
   * was never on offer — outside opening hours, off the grid, in the past, or
   * taken before the call. DOUBLE_BOOKING (23P01, below) means two callers
   * raced for a slot that WAS free, and only one could have it.
   */
  ['time is no longer available', ERROR_CODES.SLOT_UNAVAILABLE],
  /*
   * FORBIDDEN, not BUSINESS_NOT_ACTIVE: `book_slot` raises this single message
   * for a non-bookable service, an inactive service AND a business that is not
   * `active`, deliberately, so that a stranger cannot learn a company's
   * standing by probing. Mapping it to the specific code would make a claim the
   * function refused to make.
   */
  ['cannot be booked online', ERROR_CODES.FORBIDDEN],
  ['location does not belong to this business', ERROR_CODES.VALIDATION_FAILED],
  // The stock Postgres wording when an INSERT or UPDATE is refused by RLS.
  ['violates row-level security policy', ERROR_CODES.FORBIDDEN],
]

/** `... violates unique constraint "businesses_slug_key"` → the name. */
export function extractConstraintName(message: string | null | undefined): string | null {
  if (!message) return null
  const quoted = /constraint\s+"([^"]+)"/i.exec(message)
  if (quoted?.[1]) return quoted[1]
  // Postgres omits the word "constraint" for index-backed unique violations
  // raised inside some triggers: `duplicate key value violates ... "idx_name"`.
  const bare = /violates[^"]*"([^"]+)"/i.exec(message)
  return bare?.[1] ?? null
}

function matchMessageSignature(message: string | null | undefined): ErrorCode | null {
  if (!message) return null
  const haystack = message.toLowerCase()
  for (const [fragment, code] of MESSAGE_SIGNATURES) {
    if (haystack.includes(fragment)) return code
  }
  return null
}

// ─── The mapper ─────────────────────────────────────────────────────────────

function isPostgrestError(value: unknown): value is PostgrestError {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return typeof candidate['message'] === 'string' && 'code' in candidate && 'details' in candidate
}

/**
 * PostgrestError → AvexaApiError.
 *
 * Resolution order, most specific first:
 *   1. the constraint name (stable, migration-owned)
 *   2. the raised-exception prose (fragile, so it only refines a generic code)
 *   3. the SQLSTATE / PostgREST code
 *   4. INTERNAL_ERROR
 */
export function mapPostgrestError(error: PostgrestError): AvexaApiError {
  const sqlState = error.code ?? ''
  const constraint = extractConstraintName(error.message) ?? extractConstraintName(error.details)

  const details: ErrorDetails = {}
  if (sqlState) details['sqlState'] = sqlState
  if (constraint) details['constraint'] = constraint

  const byConstraint = constraint ? CONSTRAINT_CODES[constraint] : undefined
  if (byConstraint) return new AvexaApiError(byConstraint, error.message, details, error)

  const bySignature = matchMessageSignature(error.message)
  if (bySignature) return new AvexaApiError(bySignature, error.message, details, error)

  const bySqlState = SQLSTATE_CODES[sqlState]
  if (bySqlState) return new AvexaApiError(bySqlState, error.message, details, error)

  return new AvexaApiError(ERROR_CODES.INTERNAL_ERROR, error.message, details, error)
}

/**
 * Anything thrown anywhere in this package → AvexaApiError.
 *
 * Written defensively: a `catch` receives whatever was thrown, which on a phone
 * with no signal is a `TypeError` from fetch and not a database error at all.
 */
export function toAvexaError(value: unknown): AvexaApiError {
  if (isAvexaApiError(value)) return value
  if (isPostgrestError(value)) return mapPostgrestError(value)

  // supabase-js AuthError: `{ name, message, status }`.
  if (typeof value === 'object' && value !== null && 'status' in value && 'message' in value) {
    const status = (value as { status: unknown }).status
    const message = String((value as { message: unknown }).message)
    if (status === 401) {
      return new AvexaApiError(ERROR_CODES.UNAUTHENTICATED, message, { status }, value)
    }
    if (status === 403) {
      return new AvexaApiError(ERROR_CODES.FORBIDDEN, message, { status }, value)
    }
    if (status === 429) {
      return new AvexaApiError(ERROR_CODES.RATE_LIMITED, message, { status }, value)
    }
    if (typeof status === 'number' && status >= 500) {
      return new AvexaApiError(ERROR_CODES.SERVICE_UNAVAILABLE, message, { status }, value)
    }
  }

  // `fetch` rejects with a TypeError when the request never reached the server.
  // Retryable, and the only failure the user can actually do something about.
  if (value instanceof TypeError) {
    return new AvexaApiError(ERROR_CODES.NETWORK_ERROR, value.message, {}, value)
  }

  if (value instanceof Error) {
    return new AvexaApiError(ERROR_CODES.INTERNAL_ERROR, value.message, {}, value)
  }

  return new AvexaApiError(ERROR_CODES.INTERNAL_ERROR, 'Unknown error', {}, value)
}

/** Raise a typed error from this package's own preconditions. */
export function avexaError(
  code: ErrorCode,
  message: string,
  details: ErrorDetails = {},
): AvexaApiError {
  return new AvexaApiError(code, message, details)
}
